import {max, min, differenceInCalendarDays, setHours, setMinutes, getHours} from 'date-fns';
import {match} from 'ts-pattern';

import {RentalResourceCalendarEvent} from '../types/RentalResourceCalendarEvent';
import {calculateRentalTimePosition} from './calculateRentalTimePosition';

interface GetRentalEventBarLayoutParams {
  event: RentalResourceCalendarEvent;
  monthStart: Date;
  monthEnd: Date;
  daysInMonthCount: number;
  dayColumnWidth?: number;
  useQuarterPositioning?: boolean;
}

/**
 * Calculates the left and width positions for an event bar in a rental month view calendar.
 *
 * ## Positioning and Quarter Alignment Process
 *
 * The positioning system works in several steps to ensure events are visually aligned with quarter boundaries:
 *
 * ### 1. Grid Structure
 * - Each day is divided into 8 visual quarters (3-hour periods: 0-3h, 3-6h, 6-9h, 9-12h, 12-15h, 15-18h, 18-21h, 21-24h)
 * - Grid has `daysInMonth * 8` columns, each `DAY_COLUMN_WIDTH / 8` pixels wide
 * - Example: If `DAY_COLUMN_WIDTH = 72px`, each quarter = 9px
 *
 * ### 2. Time Rounding Process
 * When `useQuarterPositioning` is enabled:
 * - Original event time (e.g., 7:28) is passed to `calculateRentalTimePosition`
 * - Time gets rounded to hour-based quarter (7:28 → 6:00)
 *
 * ### 3. Position Calculation
 * ```typescript
 * // For event at 7:28 (rounded to quarter 6:00):
 * const hours = 6 (rounded from 7:28)
 * const quarterOfDay = 2 (6h falls into third quarter: 6-9h)
 * const quarterIndex = dayIndex * 8 + quarterOfDay
 * ```
 *
 * ### 4. Pixel Positioning
 * ```typescript
 * const leftPixels = quarterIndex * dayColumnWidth
 * // leftPixels = 2 * 9 = 18px from day start (third quarter)
 * ```
 *
 * ### 5. Quarter Mapping
 * | Time Range | Quarter | Position | Pixels (for 72px day) |
 * |------------|---------|----------|----------------------|
 * | 0:00-2:59  | Q0      | 0.0-1.0  | 0-9px               |
 * | 3:00-5:59  | Q1      | 1.0-2.0  | 9-18px              |
 * | 6:00-8:59  | Q2      | 2.0-3.0  | 18-27px             |
 * | 9:00-11:59 | Q3      | 3.0-4.0  | 27-36px             |
 * | 12:00-14:59| Q4      | 4.0-5.0  | 36-45px             |
 * | 15:00-17:59| Q5      | 5.0-6.0  | 45-54px             |
 * | 18:00-20:59| Q6      | 6.0-7.0  | 54-63px             |
 * | 21:00-23:59| Q7      | 7.0-8.0  | 63-72px             |
 *
 * This ensures events are precisely aligned with the visual quarter grid boundaries.
 *
 * @param params - Object containing event, monthStart, monthEnd, daysInMonthCount, dayColumnWidth, and useQuarterPositioning
 * @returns Object with left and width as percentages (when dayColumnWidth not provided) or pixels (when provided)
 *
 * @example
 * // Basic usage with quarter positioning
 * getRentalEventBarLayout({
 *   event: { start: new Date('2024-06-15 07:28'), end: new Date('2024-06-15 14:45') },
 *   monthStart: new Date('2024-06-01'),
 *   monthEnd: new Date('2024-06-30'),
 *   daysInMonthCount: 30,
 *   dayColumnWidth: 9, // Quarter width (72px day / 8)
 *   useQuarterPositioning: true
 * })
 * // => { left: 18, width: 27 } (6:00 → 15:00 span in quarters)
 */
export function getRentalEventBarLayout(params: GetRentalEventBarLayoutParams) {
  const eventStart = max([params.event.start, params.monthStart]);
  const eventEnd = min([params.event.end, params.monthEnd]);

  // Keep original times for actual duration, but calculate visual position based on quarters if enabled
  const {visualStart, visualEnd} = match(params.useQuarterPositioning)
    .with(true, () => {
      const startPos = calculateRentalTimePosition(eventStart);
      const endPos = calculateRentalTimePosition(eventEnd);
      return {
        visualStart: setMinutes(setHours(eventStart, startPos.hours), startPos.minutes),
        visualEnd: setMinutes(setHours(eventEnd, endPos.hours), endPos.minutes),
      };
    })
    .otherwise(() => ({
      visualStart: eventStart,
      visualEnd: eventEnd,
    }));

  // Calculate quarter-based positioning using discrete quarter alignment
  const getQuarterIndex = (date: Date): number => {
    const dayIndex = Math.max(0, differenceInCalendarDays(date, params.monthStart));
    const hours = getHours(date);

    // Map time to one of 8 discrete quarters per day
    // Each quarter represents 3 hours: Q0(0-3h), Q1(3-6h), Q2(6-9h), Q3(9-12h), Q4(12-15h), Q5(15-18h), Q6(18-21h), Q7(21-24h)
    const quarterOfDay = match(hours)
      .when(
        (h) => h < 3,
        () => 0
      )
      .when(
        (h) => h < 6,
        () => 1
      )
      .when(
        (h) => h < 9,
        () => 2
      )
      .when(
        (h) => h < 12,
        () => 3
      )
      .when(
        (h) => h < 15,
        () => 4
      )
      .when(
        (h) => h < 18,
        () => 5
      )
      .when(
        (h) => h < 21,
        () => 6
      )
      .otherwise(() => 7);

    // Return integer quarter index for precise grid alignment
    return dayIndex * 8 + quarterOfDay;
  };

  const startQuarterIndex = getQuarterIndex(visualStart);
  const endQuarterIndex = getQuarterIndex(visualEnd);

  return match(params.dayColumnWidth)
    .when(
      (dayColumnWidth): dayColumnWidth is number => typeof dayColumnWidth === 'number',
      (dayColumnWidth) => {
        // For pixel-based layout with quarter columns
        const leftPixels = startQuarterIndex * dayColumnWidth;
        const widthPixels = Math.max(
          dayColumnWidth,
          (endQuarterIndex - startQuarterIndex) * dayColumnWidth
        );

        return {left: leftPixels, width: widthPixels};
      }
    )
    .otherwise(() => {
      // For percentage-based layout with quarters
      const totalQuarters = params.daysInMonthCount * 8;
      const leftPercent = (startQuarterIndex / totalQuarters) * 100;
      const widthPercent = ((endQuarterIndex - startQuarterIndex) / totalQuarters) * 100;

      return {left: leftPercent, width: Math.max(widthPercent, (1 / totalQuarters) * 100)};
    });
}
